import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Card,
  CardContent,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  Chip,
  Stack
} from '@mui/material';
import {
  AddCircleOutline,
  ContentCopy,
  GroupAdd,
  Info
} from '@mui/icons-material';

interface VersionManagementDialogProps {
  open: boolean;
  onClose: () => void;
  currentVersion: string;
  videoId: string;
  userId: string;
  onVersionAction: (action: 'create_new_reset' | 'create_new_keep' | 'collaborative', version: string) => void;
  versionCreatedAt?: string; // 新增：版本建立時間
}

const VersionManagementDialog: React.FC<VersionManagementDialogProps> = ({
  open,
  onClose,
  currentVersion,
  videoId,
  userId,
  onVersionAction,
  versionCreatedAt
}) => {
  const [selectedAction, setSelectedAction] = useState<'create_new_reset' | 'create_new_keep' | 'collaborative'>('create_new_reset');
  const [nextVersion, setNextVersion] = useState('');

  // 輔助函數：確保版本號格式為 V + 數字
  const formatVersion = (version: string | undefined) => {
    if (!version) return 'V1';
    if (version.startsWith('V') || version.startsWith('v')) {
      return version.toUpperCase();
    }
    return `V${version}`;
  };

  // 輔助函數：格式化日期時間為 YYYY/MM/DD HH:MM 格式
  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}/${month}/${day} ${hours}:${minutes}`;
    } catch (error) {
      console.error('日期格式化錯誤:', error);
      return '';
    }
  };

  // 計算下一個版本號
  useEffect(() => {
    if (currentVersion) {
      // 處理不同格式的版本號：V3, v3, 3
      const versionMatch = currentVersion.match(/[Vv]?(\d+)$/);
      if (versionMatch) {
        const currentVersionNum = parseInt(versionMatch[1]);
        setNextVersion(`V${currentVersionNum + 1}`);
      } else {
        setNextVersion('V2');
      }
    } else {
      setNextVersion('V2');
    }
  }, [currentVersion]);

  const handleAction = () => {
    onVersionAction(selectedAction, nextVersion);
    onClose();
  };

  const handleClose = () => {
    setSelectedAction('create_new_reset');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <Info color="primary" />
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          版本管理
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                當前版本：
              </Typography>
              <Chip label={`${userId.split('@')[0]}-${formatVersion(currentVersion)}`} color="primary" size="small" />
            </Box>
            {versionCreatedAt && (
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                建立：{formatDateTime(versionCreatedAt)}
              </Typography>
            )}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            請選擇您要執行的版本操作：
          </Typography>
        </Box>

        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <RadioGroup
            value={selectedAction}
            onChange={(e) => setSelectedAction(e.target.value as any)}
            sx={{ gap: 2 }}
          >
            {/* 選項1：建立全新影片重點（重點歸零） */}
            <Card 
              sx={{ 
                border: selectedAction === 'create_new_reset' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                backgroundColor: selectedAction === 'create_new_reset' ? '#f3f9ff' : 'white',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f8f9ff'
                }
              }}
              onClick={() => setSelectedAction('create_new_reset')}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <FormControlLabel
                  value="create_new_reset"
                  control={<Radio />}
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <AddCircleOutline color="primary" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          建立全新影片重點（重點歸零）
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        原 {userId.split('@')[0]}-{formatVersion(currentVersion)} → 更新成 {userId.split('@')[0]}-{nextVersion}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                        重點歸零，重新開始標記
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </CardContent>
            </Card>

            {/* 選項2：建立全新影片重點（保留重點） */}
            <Card 
              sx={{ 
                border: selectedAction === 'create_new_keep' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                backgroundColor: selectedAction === 'create_new_keep' ? '#f3f9ff' : 'white',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f8f9ff'
                }
              }}
              onClick={() => setSelectedAction('create_new_keep')}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <FormControlLabel
                  value="create_new_keep"
                  control={<Radio />}
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <ContentCopy color="primary" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          建立全新影片重點（保留重點）
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        原 {userId.split('@')[0]}-{formatVersion(currentVersion)} → 更新成 {userId.split('@')[0]}-{nextVersion}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                        保留 {userId.split('@')[0]}-{formatVersion(currentVersion)} 所有重點，繼續編輯
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </CardContent>
            </Card>

            {/* 選項3：開放協同作業 */}
            <Card 
              sx={{ 
                border: selectedAction === 'collaborative' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                backgroundColor: selectedAction === 'collaborative' ? '#f3f9ff' : 'white',
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#1976d2',
                  backgroundColor: '#f8f9ff'
                }
              }}
              onClick={() => setSelectedAction('collaborative')}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <FormControlLabel
                  value="collaborative"
                  control={<Radio />}
                  label={
                    <Box sx={{ ml: 1 }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <GroupAdd color="primary" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          開放協同作業
                        </Typography>
                      </Stack>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                        開放所有用戶ID都能共同修改 {userId.split('@')[0]}-{formatVersion(currentVersion)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: 0.5 }}>
                        多人協作編輯，實時同步
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%' }}
                />
              </CardContent>
            </Card>
          </RadioGroup>
        </FormControl>

        {/* 警告提示 */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>注意：</strong>
            {selectedAction === 'create_new_reset' && ` 建立全新影片重點後，所有重點將被清空，請確認是否繼續。`}
            {selectedAction === 'create_new_keep' && ` 建立全新影片重點後，將保留所有現有重點，可以繼續編輯。`}
            {selectedAction === 'collaborative' && ` 開放協同作業後，其他用戶將能夠修改 ${userId.split('@')[0]}-${formatVersion(currentVersion)} 的重點，請謹慎操作。`}
          </Typography>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{ mr: 1 }}
        >
          取消
        </Button>
        <Button 
          onClick={handleAction}
          variant="contained"
          color="primary"
          sx={{ 
            minWidth: 120,
            fontWeight: 600
          }}
        >
          {selectedAction === 'create_new_reset' && '建立全新影片重點'}
          {selectedAction === 'create_new_keep' && '建立全新影片重點'}
          {selectedAction === 'collaborative' && '開放協同作業'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VersionManagementDialog;
